import React from "react";
import { Grid } from "@mui/material";
import { GetCasesDocument, useCloseCaseMutation } from "~/operations";
import { EmptySection } from "~/components/vulnerabilities";
import { ListCaseEdges } from "~/pages/tickets/types";
import { getError } from "~/lib/handle-error";
import { useSnackbar } from "notistack";
import { TicketsTable } from "~/pages/tickets/tickets/TicketsTable/TicketsTable";
import { SpaceOrWorkspaceScope, useScope } from "~/hooks/useScope";
import { TicketsSkeleton } from "~/pages/tickets/tickets/TicketsPage/TicketsSkeleton/TicketsSkeleton";
import { useCanCloseCase } from "~/lib/iam/actions/mondoo/policy/ExtendedHub/CloseCase";
import { PaginateProps } from "~/components/pagination/paginate";

type OpenCasesProps = {
  isLoading: boolean;
  openCases: ListCaseEdges;
  scope: SpaceOrWorkspaceScope;
  paginateProps: PaginateProps;
};

export const OpenTickets = ({
  openCases,
  isLoading,
  scope,
  paginateProps,
}: OpenCasesProps) => {
  const { spaceScope } = useScope();
  const { enqueueSnackbar } = useSnackbar();
  const [closeCase, { loading: closeCaseLoading }] = useCloseCaseMutation({
    refetchQueries: [GetCasesDocument],
  });
  const canCloseCase = useCanCloseCase(spaceScope);

  const handleCloseCase = async (caseMrn: string) => {
    if (!canCloseCase) return;

    try {
      await closeCase({
        variables: {
          input: {
            mrn: caseMrn,
          },
        },
      });
    } catch (e) {
      const errorMessage = getError(e);
      enqueueSnackbar(
        `Something went wrong while attempting to close the case: ${errorMessage}`,
        { variant: "error" },
      );
    }
  };

  const isEmpty = openCases.length === 0;

  if (isLoading) {
    return (
      <Grid size={12}>
        <TicketsSkeleton />
      </Grid>
    );
  }

  return (
    <Grid size={12}>
      {isEmpty && (
        <EmptySection
          id="open-cases-empty"
          text="There are currently no open tickets."
        />
      )}
      {!isEmpty && (
        <TicketsTable
          isLoading={isLoading}
          paginateProps={paginateProps}
          tickets={openCases}
          onClose={handleCloseCase}
          isOpen
          scope={scope}
        />
      )}
    </Grid>
  );
};
